import { getExpenseTypeOrder, ExpenseType } from '../constants/expense-types';
import { ILineItem } from '../types/line-item.types';

export { getExpenseTypeOrder };

export const sortLineItemsBySummaryOrder = (lineItems: ILineItem[]): ILineItem[] => {
    return lineItems
        .map((lineItem, index) => ({ lineItem, index }))
        .sort((a, b) => {
            const orderA = getExpenseTypeOrder(a.lineItem.expenseType);
            const orderB = getExpenseTypeOrder(b.lineItem.expenseType);

            if (orderA !== orderB) {
                return orderA - orderB;
            }

            return a.index - b.index;
        })
        .map(entry => entry.lineItem);
};

export const calculateExpenseTotals = (lineItems: ILineItem[], maxNights?: number) => {
    const totals = {
        mileage: 0,
        lodging: 0,
        flight: 0,
        transportation: 0,
        meals: 0,
        dependentCare: 0,
        caregiverLodging: 0,
        caregiverMeals: 0,
        caregiverTransportation: 0,
        caregiverFlight: 0,
        caregiverDependentCare: 0,
        caregiverTotal: 0
    };

    if (!lineItems || !Array.isArray(lineItems)) {
        return totals;
    }

    let totalNights = 0;
    let lodgingRequested = 0;

    lineItems.forEach((item) => {
        const isCaregiver = item.isCaregiverExpense === true;
        
        switch (item.expenseType) {
            case ExpenseType.LODGING:
                if (isCaregiver) {
                    totals.caregiverLodging += item.amount;
                } else {
                    totals.lodging += item.amount;
                    lodgingRequested += item.amount;
                    totalNights += (item.numberOfNights || 0);
                }
                break;
            case ExpenseType.FLIGHT:
                if (isCaregiver) {
                    totals.caregiverFlight += item.amount;
                } else {
                    totals.flight += item.amount;
                }
                break;
            case ExpenseType.TRANSPORTATION:
                if (isCaregiver) {
                    totals.caregiverTransportation += item.amount;
                } else {
                    totals.transportation += item.amount;
                }
                break;
            case ExpenseType.DEPENDENT_CARE:
                if (isCaregiver) {
                    totals.caregiverDependentCare += item.amount;
                } else {
                    totals.dependentCare += item.amount;
                }
                break;
            case ExpenseType.MEALS:
                if (isCaregiver) {
                    totals.caregiverMeals += item.amount;
                } else {
                    totals.meals += item.amount;
                }
                break;
            case ExpenseType.CAR_SERVICE:
                if (isCaregiver) {
                    totals.caregiverTransportation += item.amount;
                } else {
                    totals.transportation += item.amount;
                }
                break;
            case ExpenseType.MILEAGE:
                totals.mileage += item.amount || 0;
                break;
        }
    });

    if (maxNights && totalNights > maxNights && totalNights > 0 && lodgingRequested > 0) {
        totals.lodging = Math.round((lodgingRequested / totalNights) * maxNights * 100) / 100;
    }

    totals.caregiverTotal = totals.caregiverLodging + totals.caregiverMeals + 
                           totals.caregiverTransportation + totals.caregiverFlight + 
                           totals.caregiverDependentCare;

    // Include caregiver expenses in regular totals for API submission
    // The backend expects combined totals (regular + caregiver)
    totals.lodging += totals.caregiverLodging;
    totals.flight += totals.caregiverFlight;
    totals.transportation += totals.caregiverTransportation;
    totals.meals += totals.caregiverMeals;
    totals.dependentCare += totals.caregiverDependentCare;

    return totals;
};

export const getMileageLineItems = (lineItems: ILineItem[]): ILineItem[] => {
    return (lineItems || []).filter(li => li.expenseType === ExpenseType.MILEAGE);
};

export const sumMileageLineItemAmount = (lineItems: ILineItem[]): number => {
    return getMileageLineItems(lineItems).reduce((sum, li) => sum + (li.amount || 0), 0);
};

export const sumMileageRoundTripMiles = (lineItems: ILineItem[]): number => {
    const oneWayMiles = getMileageLineItems(lineItems).reduce((sum, li) => sum + (li.numberOfNights || 0), 0);

    return oneWayMiles * 2;
};

export const tripHasMileageToReview = (lineItems: ILineItem[]): boolean => {
    return sumMileageLineItemAmount(lineItems) > 0 || sumMileageRoundTripMiles(lineItems) > 0;
};

export const tripHasReviewableContent = (lineItems: ILineItem[]): boolean => {
    return (lineItems || []).some(li => !!li.receiptFileId) || tripHasMileageToReview(lineItems);
};

export const deriveMileageRateFromLineItems = (lineItems: ILineItem[]): number => {
    const roundTripMiles = sumMileageRoundTripMiles(lineItems);
    const amount = sumMileageLineItemAmount(lineItems);

    if (roundTripMiles > 0 && amount > 0) {
        return amount / roundTripMiles;
    }

    return 0;
};

